import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { TransactionTable } from '../components/common/TransactionTable';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { getTransactions, deleteTransaction } from '../services/transactions';
import { getCategories } from '../services/categories';

export const TransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: '', category: '', date: '' });
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        const categoryList = Array.isArray(data) ? data : (data.categories || data.results || []);
        setCategories(categoryList);
      } catch (err) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const data = await getTransactions();
        
        // Handle different response structures
        const transactionList = Array.isArray(data) ? data : (data.transactions || data.results || []);
        
        // Enrich transactions with category names if category is just an ID
        const enrichedTransactions = transactionList.map(tx => {
          if (tx.category && !isNaN(tx.category) && categories.length > 0) {
            // If category is a number (ID), find the category name
            const foundCategory = categories.find(cat => cat.id === tx.category || cat.id.toString() === tx.category);
            return { ...tx, category: foundCategory?.name || tx.category };
          }
          return tx;
        });
        
        setTransactions(enrichedTransactions);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load transactions');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [categories]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleEdit = (transactionId) => {
    navigate(`/transactions/${transactionId}/edit`);
  };

  const handleView = (transactionId) => {
    navigate(`/transactions/${transactionId}/view`);
  };

  const handleDelete = (transactionId) => {
    setTransactionToDelete(transactionId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;
    setDeleting(transactionToDelete);
    try {
      await deleteTransaction(transactionToDelete);
      setTransactions(transactions.filter(tx => tx.id !== transactionToDelete));
      setError(null);
      setShowDeleteConfirm(false);
      setTransactionToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete transaction');
    } finally {
      setDeleting(null);
    }
  };

  // Apply client-side filters to transactions
  const filteredTransactions = transactions.filter(tx => {
    // Filter by search (description)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!tx.description || !tx.description.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    
    // Filter by category
    if (filters.category && filters.category !== '') {
      // Find the selected category name from the categories list
      const selectedCategory = categories.find(cat => cat.id.toString() === filters.category.toString());
      const selectedCategoryName = selectedCategory?.name;
      
      // Compare transaction category name with selected category name
      const txCategoryName = typeof tx.category === 'object' ? tx.category?.name : tx.category;
      
      if (txCategoryName !== selectedCategoryName) {
        return false;
      }
    }
    
    // Filter by date
    if (filters.date) {
      const filterDate = filters.date; // Format: YYYY-MM-DD
      if (!tx.created_at) return false;
      // Extract just the date part from created_at (YYYY-MM-DD)
      const txDate = tx.created_at.split('T')[0];
      if (txDate !== filterDate) {
        return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading transactions...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="space-y-6 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Transactions</h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">Manage and track all your business transactions</p>
          </div>
          <Link to="/transactions/add" className="w-full sm:w-auto">
            <Button variant="primary" className="w-full sm:w-auto">
              <Plus className="w-5 h-5 mr-2 inline" />
              <span className="hidden sm:inline">Add Transaction</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              type="text"
              name="search"
              placeholder="Search by description"
              value={filters.search}
              onChange={handleFilterChange}
            />
            <SelectInput
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              options={categories.map(cat => ({ label: cat.name, value: cat.id }))}
              placeholder="Filter by category"
            />
            <FormInput
              type="date"
              name="date"
              value={filters.date}
              onChange={handleFilterChange}
            />
          </div>
        </Card>

        {/* Transactions Table */}
        {filteredTransactions.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                {transactions.length === 0 
                  ? 'No transactions found. Create your first transaction.'
                  : 'No transactions match your filters.'}
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <TransactionTable 
              transactions={filteredTransactions}
              onEdit={handleEdit}
              onView={handleView}
              onDelete={handleDelete}
            />
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="max-w-md">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Transaction</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete this transaction? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button 
                  variant="danger" 
                  onClick={confirmDelete}
                  disabled={deleting !== null}
                  className="flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setTransactionToDelete(null);
                  }}
                  disabled={deleting !== null}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
